import React from 'react';
import { Box, Grid, Divider, Paper, Typography, Chip } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import ViewQuiltOutlinedIcon from '@mui/icons-material/ViewQuiltOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SpellcheckOutlinedIcon from '@mui/icons-material/SpellcheckOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SubtitlesOutlinedIcon from '@mui/icons-material/SubtitlesOutlined';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';

import patchNotes from '../../config/patchNotes';

const ICONS = {
  image: ImageOutlinedIcon,
  machineTranslation: TranslateOutlinedIcon,
  fieldMenu: TuneOutlinedIcon,
  templates: ViewQuiltOutlinedIcon,
  markup: CodeOutlinedIcon,
  bulkDelete: DeleteSweepOutlinedIcon,
  adminLink: OpenInNewOutlinedIcon,
  darkMode: DarkModeOutlinedIcon,
  texts: SpellcheckOutlinedIcon,
  ai: AutoAwesomeOutlinedIcon,
  altText: SubtitlesOutlinedIcon,
  sort: SortOutlinedIcon,
};

const styles = {
  sheet: (theme) => ({
    maxWidth: 760,
    marginInline: 'auto',
    padding: { xs: 2.5, sm: 4 },
    marginBottom: 3,
  }),
  releaseHead: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 1.5,
    marginBottom: 4,
  },
  releaseTitle: {
    fontWeight: 500,
  },
  tag: (theme) => ({
    backgroundColor: 'transparent',
    color: theme.palette.secondary.main,
    border: `1px solid ${theme.palette.secondary.main}`,
    fontWeight: 500,
  }),
  // one entry per row; the sheet's width caps the line length
  list: {
    display: 'grid',
    rowGap: 3.5,
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
  },
  iconDisc: (theme) => ({
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.secondary.main,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : theme.palette.action.hover,
    flexShrink: 0,
  }),
  title: {
    fontWeight: 500,
    lineHeight: 1.3,
    marginBottom: 0.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexWrap: 'wrap',
  },
  // the sidebar's BETA chip look
  badge: (theme) => ({
    backgroundColor: 'transparent',
    color: theme.palette.secondary.main,
    border: `1px solid ${theme.palette.secondary.main}`,
    fontWeight: 500,
    fontSize: '0.6rem',
    height: 16,
    '& .MuiChip-label': { px: '6px' },
  }),
  text: {
    color: 'text.secondary',
  },
};

const PatchNotesPage = () => (
  <Grid container spacing={3} alignItems="flex-start" className="page-content">
    <Grid size={12} className="page-content">
      <Typography variant="h4" component="h2">Patch notes</Typography>
      <Divider className="divider-white" />
    </Grid>
    <Grid size={12}>
      {patchNotes.map((release) => (
        <Paper key={release.id} sx={styles.sheet}>
          <Box sx={styles.releaseHead}>
            <Typography variant="h5" component="h3" sx={styles.releaseTitle}>{release.title}</Typography>
            {(release.tags || []).map((tag) => <Chip key={tag} label={tag} size="small" sx={styles.tag} />)}
          </Box>
          <Box sx={styles.list}>
            {release.items.map((item) => {
              const Icon = ICONS[item.icon];
              return (
                <Box key={item.title} sx={styles.item}>
                  <Box sx={styles.iconDisc}>{Icon && <Icon fontSize="small" />}</Box>
                  <Box>
                    <Typography variant="subtitle1" component="h4" sx={styles.title}>
                      {item.title}
                      {item.badge && <Chip label={item.badge} size="small" sx={styles.badge} />}
                    </Typography>
                    <Typography variant="body2" sx={styles.text}>{item.text}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      ))}
    </Grid>
  </Grid>
);

export default PatchNotesPage;
